/**
 * Briques d'affichage partagées par le tableau de bord (vue d'ensemble) et la
 * page Statistique (détail).
 *
 * Parti pris minimal : aucune bordure, aucun cadre. Les surfaces sont des
 * `.pro-card` (blanc + rayon, sans trait) posées sur le crème du fond, et la
 * séparation se fait par le VIDE. Seuls les tableaux denses gardent un filet
 * de ligne, sans quoi une colonne de montants devient illisible.
 *
 * Aucune logique métier ici : les deux pages lisent `useProDashboard`, qui
 * délègue le calcul à `@erp/domain/pro`.
 */

import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import type { CommandeStatus, DevisStatus, ProClientStatRow } from '@erp/domain/pro';
import { formatTND } from '@/lib/format';

/** Palette pastel des anneaux (spec Figma). */
export const ORDER_STATUS_COLORS: Record<CommandeStatus, string> = {
  en_attente: '#FFEA7C',
  confirmee: '#C8FF8C',
  emballee: '#C8B8FF',
  expediee: '#FFCD8C',
  livree: '#8CE6C8',
  retournee: '#D9D9D9',
  rejetee: '#FFB2B2',
};

export const DEVIS_STATUS_COLORS: Record<DevisStatus, string> = {
  en_attente: '#FFEA7C',
  valide: '#C8FF8C',
  rejetee: '#FFB2B2',
};

export const fmtInt = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n));
export const fmtPct = (n: number) => `${n.toFixed(1).replace('.', ',')} %`;

/** Libellé de mois localisé à partir d'une clé `YYYY-MM`. */
export function makeMonthLabel(language: string): (key: string) => string {
  const formatter = new Intl.DateTimeFormat(language || 'fr', { month: 'short', year: '2-digit' });
  return (key: string) => {
    const [year, month] = key.split('-').map(Number);
    if (!year || !month) return key;
    return formatter.format(new Date(year, month - 1, 1));
  };
}

/** Jours de la semaine localisés, lundi en tête. */
export function makeWeekdayLabels(language: string): string[] {
  const formatter = new Intl.DateTimeFormat(language || 'fr', { weekday: 'short' });
  // 2024-01-01 est un lundi : sept jours consécutifs à partir de là.
  return Array.from({ length: 7 }, (_, i) => formatter.format(new Date(2024, 0, 1 + i)));
}

/** Nom affichable d'une ligne agrégée (le seau « sans client » a `clientId` null). */
export function clientLabel(
  row: ProClientStatRow,
  names: Map<string, string>,
  t: (key: string, fallback: string) => string,
): string {
  if (!row.clientId) return t('pro.statistique.no_client', 'Sans client');
  return names.get(row.clientId) ?? t('pro.statistique.unknown_client', 'Client supprimé');
}

export function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span
      className="inline-flex items-center gap-2 text-[12px]"
      style={{ color: 'var(--pro-text-light)', fontFamily: 'Figtree' }}
    >
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

export function ChartSkeleton() {
  return <div className="h-full w-full rounded-xl animate-pulse" style={{ background: 'var(--pro-grey-subtle)' }} />;
}

/** Infobulle du graphique : ombre portée seule, aucun contour. */
export function MonthTooltip({
  active,
  payload,
  label,
  monthLabel,
  validatedLabel,
  pendingLabel,
  ordersLabel,
}: {
  active?: boolean;
  payload?: Array<{ payload: { commissionValidated: number; commissionPending: number; orders: number } }>;
  label?: string;
  monthLabel: (key: string) => string;
  validatedLabel: string;
  pendingLabel: string;
  ordersLabel: string;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <div
      className="rounded-xl px-3.5 py-2.5 shadow-[0_6px_20px_rgba(42,29,20,0.12)] space-y-1.5"
      style={{ background: '#fff', fontFamily: 'Figtree' }}
    >
      <div className="text-[11px]" style={{ color: 'var(--pro-text-light)' }}>
        {label ? monthLabel(label) : ''}
      </div>
      <TooltipRow color="#FF6B1E" label={validatedLabel} value={formatTND(point.commissionValidated)} />
      <TooltipRow color="#F6A723" label={pendingLabel} value={formatTND(point.commissionPending)} />
      <div className="text-[11px] pt-0.5" style={{ color: 'var(--pro-text-light)' }}>
        {ordersLabel} : {fmtInt(point.orders)}
      </div>
    </div>
  );
}

function TooltipRow({ color, label, value }: { color: string; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
      <span style={{ color: 'var(--pro-text-grey)' }}>{label}</span>
      <span className="ml-auto font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
        {value}
      </span>
    </div>
  );
}

export function DonutCard<S extends string>({
  title,
  slices,
  colors,
  labelFor,
  emptyLabel,
  action,
}: {
  title: string;
  slices: Array<{ status: S; count: number; share: number; amountTtc: number }>;
  colors: Record<S, string>;
  labelFor: (status: S) => string;
  emptyLabel: string;
  action?: React.ReactNode;
}) {
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);
  const visible = slices.filter((slice) => slice.count > 0);
  return (
    <div className="pro-card p-6 flex flex-col gap-5">
      <div className="flex items-center justify-between gap-2">
        <h2
          className="text-[15px] font-semibold"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree', letterSpacing: '-0.01em' }}
        >
          {title}
        </h2>
        {action}
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-7">
        <div className="w-[136px] h-[136px] shrink-0 relative">
          {total === 0 ? (
            // État vide : deux disques concentriques — une forme, pas un contour.
            <div className="h-full w-full rounded-full" style={{ background: 'var(--pro-grey-subtle)' }}>
              <div className="absolute inset-[22px] rounded-full" style={{ background: '#fff' }} />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                {/* Sans animation : recharts ne calcule la géométrie qu'au fil
                    des frames, un onglet au rAF bridé afficherait un anneau vide. */}
                <Pie
                  data={visible}
                  innerRadius={46}
                  outerRadius={68}
                  dataKey="count"
                  paddingAngle={2}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {visible.map((slice) => (
                    <Cell key={slice.status} fill={colors[slice.status] ?? '#D9D9D9'} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="pro-numeral text-[22px] font-semibold" style={{ color: 'var(--pro-text-dark)' }}>
              {fmtInt(total)}
            </span>
          </div>
        </div>
        <div className="flex-1 w-full space-y-3">
          {total === 0 ? (
            <p className="text-[13px]" style={{ color: 'var(--pro-text-light)' }}>
              {emptyLabel}
            </p>
          ) : (
            visible.map((slice) => (
              <div key={slice.status} className="flex items-center gap-2.5 text-[13px]">
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: colors[slice.status] ?? '#D9D9D9' }}
                />
                <span className="truncate" style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}>
                  {labelFor(slice.status)}
                </span>
                <span className="pro-numeral ml-auto" style={{ color: 'var(--pro-text-light)' }}>
                  {fmtInt(slice.count)} · {Math.round(slice.share)} %
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function ShareBar({ pct }: { pct: number }) {
  return (
    <div className="flex items-center gap-2.5 justify-end">
      <div
        className="w-14 h-[3px] rounded-full overflow-hidden shrink-0"
        style={{ background: 'var(--pro-grey-subtle)' }}
      >
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: 'var(--pro-primary)' }}
        />
      </div>
      <span className="pro-numeral text-[12px] w-[40px] text-right" style={{ color: 'var(--pro-text-light)' }}>
        {Math.round(pct)} %
      </span>
    </div>
  );
}

/** Ligne client en mobile : de l'espace pour séparer, pas de cadre. */
export function ClientStatCard({
  row,
  name,
  onOpen,
}: {
  row: ProClientStatRow;
  name: string;
  onOpen?: () => void;
}) {
  const { t } = useTranslation();
  const Wrapper = onOpen ? 'button' : 'div';
  return (
    <Wrapper
      {...(onOpen ? { type: 'button' as const, onClick: onOpen } : {})}
      className="w-full text-left py-4 space-y-3 active:opacity-70"
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-[14px] font-semibold truncate"
          style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
        >
          {name}
        </span>
        <span className="pro-numeral text-[12px] shrink-0" style={{ color: 'var(--pro-text-light)' }}>
          {Math.round(row.revenueShare)} %
        </span>
      </div>
      <div className="grid grid-cols-2 gap-y-3 gap-x-4">
        <Fact label={t('pro.statistique.col_revenue', 'CA TTC')} value={formatTND(row.revenueTtc)} />
        <Fact label={t('pro.statistique.col_commission', 'Commission')} value={formatTND(row.commission)} accent />
        <Fact label={t('pro.statistique.col_collected', 'Encaissé')} value={formatTND(row.collected)} />
        <Fact
          label={t('pro.statistique.col_orders', 'Commandes')}
          value={`${fmtInt(row.ordersCount)} · ${fmtInt(row.quotesCount)} ${t('pro.statistique.col_quotes', 'Devis').toLowerCase()}`}
        />
      </div>
    </Wrapper>
  );
}

function Fact({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-[11px]" style={{ color: 'var(--pro-text-light)', fontFamily: 'Figtree' }}>
        {label}
      </div>
      <div
        className="pro-numeral text-[14px] font-semibold mt-0.5"
        style={{ color: accent ? 'var(--pro-primary)' : 'var(--pro-text-dark)' }}
      >
        {value}
      </div>
    </div>
  );
}

export function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`pb-3 px-3 text-[11.5px] font-medium whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}
      style={{ color: 'var(--pro-text-light)', fontFamily: 'Figtree' }}
    >
      {children}
    </th>
  );
}
